import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const emptyForm = { name: '', description: '', price: '', emoji: '🎁', swatch: '#DBEAFE', is_active: true };
const inputStyle = { width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box' };

function ComponentSelector({ menuItems, components, onChange }) {
  const add = (itemId) => {
    const item = menuItems.find(m => m.id === itemId);
    if (!item) return;
    const exists = components.find(c => c.menu_item_id === itemId);
    if (exists) onChange(components.map(c => c.menu_item_id === itemId ? { ...c, qty: c.qty + 1 } : c));
    else onChange([...components, { menu_item_id: itemId, qty: 1, item_name: item.name, item_emoji: item.emoji }]);
  };
  const remove = (itemId) => onChange(components.filter(c => c.menu_item_id !== itemId));
  const setQty = (itemId, qty) => onChange(components.map(c => c.menu_item_id === itemId ? { ...c, qty: Math.max(1, +qty) } : c));

  return (
    <div>
      <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 6 }}>Komponen Paket</label>
      <select onChange={e => { if (e.target.value) { add(e.target.value); e.target.value = ''; } }}
        style={{ ...inputStyle, marginBottom: 10 }}>
        <option value="">+ Tambah item ke paket...</option>
        {menuItems.map(m => <option key={m.id} value={m.id}>{m.emoji} {m.name} — Rp {(m.base_price||0).toLocaleString('id-ID')}</option>)}
      </select>
      {components.map(c => (
        <div key={c.menu_item_id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <span style={{ flex: 1, fontSize: '.84rem' }}>{c.item_emoji} {c.item_name}</span>
          <input type="number" min="1" value={c.qty} onChange={e => setQty(c.menu_item_id, e.target.value)}
            style={{ width: 60, border: '1.5px solid #DDD', borderRadius: 6, padding: '5px 8px', fontSize: '.82rem', outline: 'none', textAlign: 'center' }} />
          <span style={{ fontSize: '.74rem', color: 'var(--muted)' }}>pcs</span>
          <button type="button" onClick={() => remove(c.menu_item_id)} style={{ color: '#C62828', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}>×</button>
        </div>
      ))}
      {components.length === 0 && <div style={{ fontSize: '.78rem', color: 'var(--muted)', fontStyle: 'italic' }}>Belum ada komponen ditambahkan</div>}
    </div>
  );
}

export default function MenuPaket() {
  const [packages, setPackages]   = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [form, setForm]           = useState(emptyForm);
  const [components, setComponents] = useState([]);
  const [editId, setEditId]       = useState(null);
  const [showForm, setShowForm]   = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    const [pkgRes, menuRes] = await Promise.all([
      supabase.from('packages').select('*, package_items(menu_item_id, qty, menu_items(name,emoji,hpp,base_price))').order('name'),
      supabase.from('menu_items').select('id,name,emoji,hpp,base_price').eq('is_hidden', false).order('category').order('name'),
    ]);
    setPackages(pkgRes.data || []);
    setMenuItems(menuRes.data || []);
  }

  async function handleSave() {
    if (!form.name || !form.price) return;
    const payload = { name: form.name, description: form.description, price: +form.price, emoji: form.emoji, swatch: form.swatch, is_active: !!form.is_active };
    let pid;
    if (editId) {
      await supabase.from('packages').update(payload).eq('id', editId);
      pid = editId;
    } else {
      const { data } = await supabase.from('packages').insert(payload).select('id').single();
      pid = data.id;
    }
    await supabase.from('package_items').delete().eq('package_id', pid);
    if (components.length) {
      await supabase.from('package_items').insert(components.map(c => ({ package_id: pid, menu_item_id: c.menu_item_id, qty: c.qty })));
    }
    reset(); load();
  }

  async function handleDelete(id) {
    if (!window.confirm('Hapus paket ini?')) return;
    await supabase.from('packages').delete().eq('id', id);
    load();
  }

  async function toggleActive(pkg) {
    await supabase.from('packages').update({ is_active: !pkg.is_active }).eq('id', pkg.id);
    load();
  }

  function handleEdit(pkg) {
    setForm({ name: pkg.name, description: pkg.description || '', price: pkg.price, emoji: pkg.emoji, swatch: pkg.swatch, is_active: pkg.is_active });
    setComponents((pkg.package_items || []).map(pi => ({ menu_item_id: pi.menu_item_id, qty: pi.qty, item_name: pi.menu_items?.name || '', item_emoji: pi.menu_items?.emoji || '🍘' })));
    setEditId(pkg.id); setShowForm(true); window.scrollTo(0, 0);
  }

  function reset() { setForm(emptyForm); setComponents([]); setEditId(null); setShowForm(false); }
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // HPP paket = sum(hpp_per_item × qty). Fallback ke base_price jika hpp belum diisi.
  const calcHPP = (pkg) => (pkg.package_items || []).reduce((s, pi) => {
    const hppItem = pi.menu_items?.hpp || pi.menu_items?.base_price || 0;
    return s + hppItem * pi.qty;
  }, 0);

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>
      <style>{`@media(max-width:580px){.paket-grid{grid-template-columns:1fr!important}}`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Menu Paket</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{packages.length} paket terdaftar</div>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} style={{ padding: '9px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.84rem', fontFamily: "'DM Sans',sans-serif", cursor: 'pointer' }}>+ Buat Paket</button>
      </div>

      {showForm && (
        <div className="fade-up" style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '22px', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: 18 }}>{editId ? 'Edit Paket' : 'Paket Baru'}</div>
          <div className="paket-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Nama Paket *</label><input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Paket Hemat" style={inputStyle} /></div>
            <div><label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Harga Jual (Rp) *</label><input type="number" value={form.price} onChange={e => set('price', e.target.value)} placeholder="25000" style={inputStyle} /></div>
            <div><label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Emoji</label><input value={form.emoji} onChange={e => set('emoji', e.target.value)} style={inputStyle} /></div>
            <div>
              <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Warna</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.swatch} onChange={e => set('swatch', e.target.value)} style={{ width: 40, height: 36, border: '1.5px solid #DDD', borderRadius: 6, padding: 2, cursor: 'pointer' }} />
                <div style={{ width: 80, height: 36, borderRadius: 6, background: form.swatch, border: '1px solid #DDD' }} />
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Deskripsi</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: "'DM Sans',sans-serif" }} />
          </div>
          <div style={{ marginBottom: 14 }}><ComponentSelector menuItems={menuItems} components={components} onChange={setComponents} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', marginBottom: 16, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.is_active} onChange={e => set('is_active', e.target.checked)} /> Aktif (tampil di POS)
          </label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={reset} style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.82rem', cursor: 'pointer', color: 'var(--muted)' }}>Batal</button>
            <button onClick={handleSave} style={{ padding: '8px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.82rem', cursor: 'pointer' }}>{editId ? 'Simpan' : 'Buat Paket'}</button>
          </div>
        </div>
      )}

      {packages.map(pkg => {
        const hpp = calcHPP(pkg);
        const margin = pkg.price - hpp;
        return (
          <div key={pkg.id} style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '14px 18px', marginBottom: 10, boxShadow: 'var(--shadow)', opacity: pkg.is_active ? 1 : 0.6 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 44, height: 44, borderRadius: 8, background: pkg.swatch, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{pkg.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                  <div style={{ fontWeight: 500, fontSize: '.9rem' }}>{pkg.name} {!pkg.is_active && <span style={{ fontSize: '.68rem', color: '#999', background: '#F5F5F5', borderRadius: 4, padding: '1px 5px' }}>nonaktif</span>}</div>
                  <div style={{ fontSize: '.9rem', fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, color: 'var(--brown)' }}>Rp {(pkg.price||0).toLocaleString('id-ID')}</div>
                </div>
                <div style={{ fontSize: '.76rem', color: 'var(--muted)', marginBottom: 6 }}>
                  {(pkg.package_items || []).map(pi => {
                    const hppItem = pi.menu_items?.hpp || pi.menu_items?.base_price || 0;
                    return `${pi.menu_items?.emoji || ''} ${pi.menu_items?.name || ''} ×${pi.qty} (HPP ${(hppItem * pi.qty).toLocaleString('id-ID')})`;
                  }).join(' · ')}
                </div>
                <div style={{ fontSize: '.74rem', color: margin >= 0 ? '#1B5E20' : '#C62828' }}>
                  Margin: Rp {margin.toLocaleString('id-ID')} = Rp {pkg.price.toLocaleString('id-ID')} − Rp {hpp.toLocaleString('id-ID')} HPP
                  {hpp === 0 && <span style={{ color: '#999' }}> (HPP belum diisi di menu)</span>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => toggleActive(pkg)} style={{ padding: '4px 10px', borderRadius: 6, background: pkg.is_active ? '#FFF3E0' : '#E8F5E9', border: `1px solid ${pkg.is_active ? '#FFCC80' : '#A5D6A7'}`, color: pkg.is_active ? '#E65100' : '#1B5E20', fontSize: '.72rem', cursor: 'pointer' }}>
                {pkg.is_active ? 'Nonaktifkan' : 'Aktifkan'}
              </button>
              <button onClick={() => handleEdit(pkg)} style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.72rem', cursor: 'pointer' }}>Edit</button>
              <button onClick={() => handleDelete(pkg.id)} style={{ padding: '4px 10px', borderRadius: 6, background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', fontSize: '.72rem', cursor: 'pointer' }}>Hapus</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
